import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../../components/loader/Loader";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import {
  createAdsWhitelistItem,
  deleteAdsWhitelistItem,
  fetchAdsWhitelist,
} from "../../../../features/adsWhitelist/adsWhitelistSlice";
import { showModal } from "../../../../features/statusModal/statusModal";
import { fetchUsersByQueryAPI } from "../../../../features/userSearch/userSearchAPI";
import styles from "./controlAds.module.css";

const scopeLabels = {
  user: "لاعب",
  squad: "سكواد",
};

const getEntityId = (item) => String(item?._id || item?.id || "");
const getGameByName = (gameIds = [], name) =>
  (Array.isArray(gameIds) ? gameIds : []).find(
    (entry) => String(entry?.gameName || "").toLowerCase() === String(name),
  );
const formatGameIdentity = (entry) => {
  if (!entry) return "غير متوفر";
  const gameName = entry?.inGameName ? String(entry.inGameName) : "غير متوفر";
  const gameId = entry?.inGameId ? String(entry.inGameId) : "غير متوفر";
  return `${gameName} - ${gameId}`;
};
const getFallbackInitial = (text) => {
  const value = String(text || "").trim();
  return value ? value.charAt(0).toUpperCase() : "?";
};

const ControlAds = () => {
  const dispatch = useDispatch();
  const { items, total, fetchStatus, createStatus, deleteStatus } = useSelector(
    (state) => state.adsWhitelist,
  );

  const [listScopeFilter, setListScopeFilter] = useState("");
  const [listQuery, setListQuery] = useState("");

  const [searchScope, setSearchScope] = useState("user");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchResults, setSearchResults] = useState([]);
  const [pendingSearchAddId, setPendingSearchAddId] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState("");

  const loadWhitelist = useCallback(() => {
    dispatch(
      fetchAdsWhitelist({
        scopeType: listScopeFilter,
        query: listQuery,
        page: 1,
        limit: 100,
      }),
    );
  }, [dispatch, listScopeFilter, listQuery]);

  useEffect(() => {
    loadWhitelist();
  }, [loadWhitelist]);

  const handleSearch = useCallback(async () => {
    const query = String(searchQuery || "").trim();
    if (!query) {
      setSearchResults([]);
      return;
    }

    setSearchStatus("loading");
    try {
      const response = await fetchUsersByQueryAPI(query, searchScope);
      const data = Array.isArray(response?.data) ? response.data : [];
      setSearchResults(data);
      setSearchStatus("succeeded");
    } catch (error) {
      setSearchStatus("failed");
      dispatch(
        showModal({
          message: "فشل البحث",
          status: "error",
        }),
      );
    }
  }, [dispatch, searchQuery, searchScope]);

  const existingKeys = useMemo(() => {
    return new Set(
      (Array.isArray(items) ? items : []).map((item) => {
        if (item?.scopeType === "user" && item?.userId?._id) {
          return `user:${String(item.userId._id)}`;
        }
        if (item?.scopeType === "squad" && item?.squadId?._id) {
          return `squad:${String(item.squadId._id)}`;
        }
        return "";
      }),
    );
  }, [items]);

  const handleAddToWhitelist = useCallback(
    async (entity) => {
      const entityId = getEntityId(entity);
      if (!entityId) return;

      const payload =
        searchScope === "squad"
          ? { scopeType: "squad", squadId: entityId }
          : { scopeType: "user", userId: entityId };

      setPendingSearchAddId(entityId);
      try {
        await dispatch(createAdsWhitelistItem(payload)).unwrap();
        loadWhitelist();
      } finally {
        setPendingSearchAddId("");
      }
    },
    [dispatch, loadWhitelist, searchScope],
  );

  const handleDeleteItem = useCallback(
    async (id) => {
      if (!id) return;
      setPendingDeleteId(String(id));
      try {
        await dispatch(deleteAdsWhitelistItem(id)).unwrap();
      } finally {
        setPendingDeleteId("");
      }
    },
    [dispatch],
  );

  const renderSearchLabel = useCallback(
    (item) => {
      if (searchScope === "squad") {
        const name = item?.name || "سكواد غير معروف";
        const slug = item?.slug ? `@${item.slug}` : "";
        return `${name} ${slug}`.trim();
      }

      const name = item?.name || "لاعب غير معروف";
      const email = item?.email ? `(${item.email})` : "";
      return `${name} ${email}`.trim();
    },
    [searchScope],
  );

  const renderWhitelistLabel = useCallback((item) => {
    if (item?.scopeType === "squad") {
      const name = item?.squadId?.name || "سكواد محذوف";
      const slug = item?.squadId?.slug ? `@${item.squadId.slug}` : "";
      return `${name} ${slug}`.trim();
    }

    const name = item?.userId?.name || "لاعب محذوف";
    const email = item?.userId?.email ? `(${item.userId.email})` : "";
    return `${name} ${email}`.trim();
  }, []);

  return (
    <SettingsPagesContainer title="التحكم في الإعلانات - القائمة البيضاء" fullWidth>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>القائمة البيضاء الحالية</div>
          <div className={styles.row}>
            <select
              value={listScopeFilter}
              onChange={(event) => setListScopeFilter(event.target.value)}
              className={styles.select}
            >
              <option value="">الكل</option>
              <option value="user">اللاعبون</option>
              <option value="squad">السكوادات</option>
            </select>

            <input
              value={listQuery}
              onChange={(event) => setListQuery(event.target.value)}
              placeholder="تصفية بالاسم/البريد/المعرّف"
              className={styles.input}
            />

            <button
              type="button"
              className={styles.primaryBtn}
              onClick={loadWhitelist}
            >
              تطبيق
            </button>
          </div>

          <div className={styles.metaRow}>
            <span>إجمالي العناصر: {total}</span>
          </div>

          {fetchStatus === "loading" ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>لا توجد عناصر في القائمة البيضاء.</div>
          ) : (
            <div className={styles.list}>
              {items.map((item) => {
                const itemId = String(item?._id || "");
                return (
                  <div className={styles.listItem} key={itemId}>
                    <div className={styles.itemText}>
                      <span className={styles.itemTitle}>
                        {renderWhitelistLabel(item)}
                      </span>
                      <span className={styles.itemMeta}>
                        النوع: {scopeLabels[item?.scopeType] || item?.scopeType}
                      </span>
                      <span className={styles.itemMeta}>
                        البطولة:{" "}
                        {item?.tournamentId?.name
                          ? item.tournamentId.name
                          : "كل البطولات"}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleDeleteItem(itemId)}
                      disabled={
                        deleteStatus === "loading" && pendingDeleteId === itemId
                      }
                      className={styles.dangerBtn}
                    >
                      {deleteStatus === "loading" && pendingDeleteId === itemId
                        ? "جاري الإزالة..."
                        : "إزالة"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>إضافة لاعب أو سكواد</div>
          <div className={styles.row}>
            <select
              value={searchScope}
              onChange={(event) => {
                setSearchScope(event.target.value);
                setSearchResults([]);
              }}
              className={styles.select}
            >
              <option value="user">لاعب</option>
              <option value="squad">سكواد</option>
            </select>

            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={
                searchScope === "squad"
                  ? "ابحث عن سكواد بالاسم/المعرّف"
                  : "ابحث عن لاعب بالاسم/البريد"
              }
              className={styles.input}
            />

            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSearch}
            >
              {searchStatus === "loading" ? "جاري البحث..." : "بحث"}
            </button>
          </div>

          {searchStatus === "loading" ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : searchResults.length === 0 ? (
            <div className={styles.emptyState}>
              ستظهر نتائج البحث هنا.
            </div>
          ) : (
            <div className={styles.list}>
              {searchResults.map((item) => {
                const entityId = getEntityId(item);
                const listKey = `${searchScope}:${entityId}`;
                const alreadyAdded = existingKeys.has(listKey);
                const avatarPath =
                  searchScope === "squad"
                    ? item?.squadImageId?.path || ""
                    : item?.userImage?.path || "";

                const ffGame = getGameByName(
                  searchScope === "squad" ? item?.ownerId?.gameIds : item?.gameIds,
                  "freefire",
                );
                const pubgGame = getGameByName(
                  searchScope === "squad" ? item?.ownerId?.gameIds : item?.gameIds,
                  "pubg",
                );

                const userSquadLabel =
                  item?.hasSquad && item?.squad
                    ? `${item?.squad?.name || ""} ${item?.squad?.slug ? `@${item.squad.slug}` : ""}`.trim()
                    : "بدون سكواد";
                const ownerName = item?.ownerId?.name || "غير متوفر";

                const membersPreview = (Array.isArray(item?.members) ? item.members : [])
                  .slice(0, 3)
                  .map((member) => {
                    const memberUser = member?.userId || {};
                    const memberFf = getGameByName(memberUser?.gameIds, "freefire");
                    const memberPubg = getGameByName(memberUser?.gameIds, "pubg");
                    return {
                      key: String(
                        member?._id ||
                          `${String(memberUser?._id || "")}:${String(member?.role || "")}`,
                      ),
                      name: memberUser?.name || "عضو غير معروف",
                      ff: formatGameIdentity(memberFf),
                      pubg: formatGameIdentity(memberPubg),
                    };
                  });

                return (
                  <div className={styles.listItem} key={listKey}>
                    <div className={styles.entityMain}>
                      <div className={styles.itemText}>
                        <span className={styles.itemTitle}>
                          {renderSearchLabel(item)}
                        </span>
                        {searchScope === "user" ? (
                          <>
                            <span className={styles.itemMeta}>
                              اسم المستخدم: {item?.name || "غير متوفر"}
                            </span>
                            <span className={styles.itemMeta}>السكواد: {userSquadLabel}</span>
                            <span className={styles.itemMeta}>
                              Free Fire: {formatGameIdentity(ffGame)}
                            </span>
                            <span className={styles.itemMeta}>
                              PUBG: {formatGameIdentity(pubgGame)}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className={styles.itemMeta}>القائد: {ownerName}</span>
                            <span className={styles.itemMeta}>
                              Free Fire (القائد): {formatGameIdentity(ffGame)}
                            </span>
                            <span className={styles.itemMeta}>
                              PUBG (القائد): {formatGameIdentity(pubgGame)}
                            </span>
                            <span className={styles.itemMeta}>
                              عدد الأعضاء: {Number(item?.membersCount || 0)}
                            </span>
                            {membersPreview.map((member) => (
                              <span className={styles.itemMeta} key={member.key}>
                                {member.name} | FF: {member.ff} | PUBG: {member.pubg}
                              </span>
                            ))}
                          </>
                        )}
                      </div>
                      <div className={styles.entityAvatar}>
                        {avatarPath ? (
                          <img src={avatarPath} alt="entity-avatar" />
                        ) : (
                          <span className={styles.entityAvatarFallback}>
                            {getFallbackInitial(item?.name)}
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleAddToWhitelist(item)}
                      disabled={
                        alreadyAdded ||
                        createStatus === "loading" ||
                        pendingSearchAddId === entityId
                      }
                      className={styles.primaryBtn}
                    >
                      {alreadyAdded
                        ? "مضاف مسبقًا"
                        : createStatus === "loading" &&
                            pendingSearchAddId === entityId
                          ? "جاري الإضافة..."
                          : "إضافة"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </SettingsPagesContainer>
  );
};

export default ControlAds;
